import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { InventoryItem, TruckInventoryFilters, TruckInventoryIdCode } from 'src/app/core/models/inventory.model';
import { TruckInventoryService } from 'src/app/core/services/inventory/truck-inventory.service';
import { TruckInventoryFilterComponent } from '../truck-inventory-filter/truck-inventory-filter.component';

type FilterResponse = OverlayEventDetail<{
	filters: TruckInventoryFilters,
}>;

@Component({
	selector: 'app-truck-inventory-list',
	templateUrl: './truck-inventory-list.component.html',
	styleUrls: ['./truck-inventory-list.component.scss'],
})
export class TruckInventoryListComponent {

	pageTitle = 'Truck Inventory';
	isLoading = false;
	expandedSections = [] as number[];

	defaultFilters = {
		statusId: TruckInventoryIdCode.AVAILABLE,
		categoryId: TruckInventoryIdCode.ALL,
		equipmentId: TruckInventoryIdCode.ALL,
	} as TruckInventoryFilters;
	lastFilters: TruckInventoryFilters;

	inventoryListSource = [] as InventoryItem[];
	inventoryList = [] as InventoryItem[];

	constructor(
		private truckInventoryService: TruckInventoryService,
		private modalController: ModalController,
	) { }

	async ionViewWillEnter(): Promise<void> {
		this.isLoading = true;
		await this.initInventoryListSource();
		this.inventoryList = this.filterInventoryList([...this.inventoryListSource], this.defaultFilters);
		this.isLoading = false;
	}

	ionViewWillLeave(): void {
		this.inventoryList = [];
		this.expandedSections = [];
		this.lastFilters = this.defaultFilters;
		this.isLoading = false;
	}

	private async initInventoryListSource(): Promise<void> {
		this.inventoryListSource = (await this.truckInventoryService.getAllTruckInventory())
			.sort((a, b) => {
				if (a.categoryId === b.categoryId && a.equipId === b.equipId) {
					return a.id - b.id;
				}

				if (a.categoryId === b.categoryId && a.equipId !== b.equipId) {
					return a.equipId - b.equipId;
				}

				if (a.categoryId !== b.categoryId && a.equipId !== b.equipId) {
					return a.categoryId - b.categoryId;
				}
			});
	}

	async onClickOpenFilter(): Promise<void> {
		setTimeout(() => { // Prevent load screen flash before modal appears
			this.isLoading = true; // Hide massive DOM to prevent lag in modal
		}, 50);

		const modal = await this.modalController.create({
			component: TruckInventoryFilterComponent,
			componentProps: {
				lastFilters: this.lastFilters,
			},
			cssClass: 'full-screen-modal',
		});

		await modal.present();

		const filters = (await modal.onDidDismiss() as FilterResponse)?.data?.filters;
		this.inventoryList = this.filterInventoryList([...this.inventoryListSource], filters);

		this.isLoading = false;
	}

	filterInventoryList(list: InventoryItem[], filters?: TruckInventoryFilters): InventoryItem[] {
		if (!filters) {
			return;
		}
		this.lastFilters = filters;

		if (filters.statusId) {
			list = list.filter(i => i.statusId === filters.statusId);
		}

		if (filters.categoryId) {
			list = list.filter(i => i.categoryId === filters.categoryId);
		}

		if (filters.equipmentId) {
			list = list.filter(i => i.equipId === filters.equipmentId);
		}

		return list;
	}

	onClickToggleSection(index: number): void {
		const foundIndex = this.expandedSections.findIndex(i => i === index);
		if (foundIndex === -1) {
			this.expandedSections.push(index);
			return;
		}

		this.expandedSections.splice(foundIndex, 1);
	}

	isVisible(index: number): boolean {
		const foundIndex = this.expandedSections.findIndex(i => i === index);
		return foundIndex === -1 ? false : true;
	}
}
